import {
  gapRows,
  group,
  groupsCss,
  panel,
  panelCss,
  rowList,
  rowsCss,
} from '../armKit';
import { profileEditUrl, workEditUrl } from '../destinations';
import { profileGaps, viewedPageName } from '../profileFacts';
import { cssRule, INK } from '../surface';

import type { Row } from '../armKit';
import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';

const HEADING_CLASS = 'profile-liautotest-gap__heading';

const PROFILE_HEADING = 'Your profile is missing:';
const WORK_HEADING = 'Papers with no file attached';
const PROFILE_SOURCE = 'profile-liautotest-gap-profile';
const WORK_SOURCE = 'profile-liautotest-gap-work';

// Enough to read as an inventory the author can clear in one sitting. Every
// further row is the same instruction repeated, and a wall of them reads as a
// reprimand rather than as a list.
const WORK_LIMIT = 5;

function armCss(scope: string): string {
  return (
    panelCss(scope, { padding: '14px 18px', marginBottom: '0' }) +
    rowsCss(scope, { itemPadding: '4px 0' }) +
    groupsCss(scope) +
    cssRule(
      [`${scope} .${HEADING_CLASS}`],
      `font-size: 13px; font-weight: 700; color: ${INK}; margin: 0 0 4px;`,
    )
  );
}

function profileFieldRows(ctx: ArmContext): Row[] {
  const pageName = viewedPageName();
  if (pageName == null) return [];

  return gapRows(profileGaps(), profileEditUrl(pageName), (field) =>
    ctx.track(PROFILE_SOURCE, { extra: { field } }),
  );
}

function unattached(
  facts: Map<number, WorkFacts>,
  ctx: ArmContext,
): { workId: number; title: string }[] {
  const rendered = new Map(ctx.cards().map((card) => [card.workId, card]));

  return Array.from(facts).flatMap(([workId, fact]) => {
    // Only an explicit false is a work with no file; an unknown is a work the
    // endpoint did not describe, and listing it would be an accusation.
    if (fact.hasFile !== false) return [];
    // The card itself offers a download — a "no file" row for it would
    // contradict what the page next to it already shows.
    if (rendered.get(workId)?.hasDownloadable === true) return [];

    const title = (fact.title ?? rendered.get(workId)?.title ?? '').trim();

    return title === '' ? [] : [{ workId, title }];
  });
}

function workRows(
  facts: Map<number, WorkFacts>,
  ctx: ArmContext,
): { heading: string; rows: Row[] } {
  const works = unattached(facts, ctx);
  const shown = works.slice(0, WORK_LIMIT);

  return {
    heading:
      works.length > shown.length
        ? `${WORK_HEADING} (${shown.length} of ${works.length})`
        : WORK_HEADING,
    rows: shown.map((work) => ({
      title: work.title,
      url: workEditUrl(work.workId),
      onClick: () => ctx.track(WORK_SOURCE, { entityId: work.workId }),
    })),
  };
}

function groupWith(kind: string, heading: string, rows: Row[]): HTMLElement {
  const element = group(kind);

  const label = document.createElement('p');
  label.className = HEADING_CLASS;
  label.textContent = heading;
  element.appendChild(label);
  element.appendChild(rowList(rows));

  return element;
}

export const profileGapAudit: ArmSpec = {
  onReady: async (ctx) => {
    let body: HTMLElement | null = null;

    const mount = (): HTMLElement | null => {
      if (body != null) return body;

      const slot = ctx.mountSlot('columnTop');
      if (slot == null) return null;

      ctx.injectStyles(armCss(ctx.scope));

      const built = panel();
      slot.appendChild(built.root);
      body = built.body;

      return body;
    };

    const fields = profileFieldRows(ctx);
    if (fields.length > 0) {
      mount()?.appendChild(groupWith('profile', PROFILE_HEADING, fields));
    }

    const works = workRows(await ctx.workFacts(), ctx);
    if (works.rows.length === 0) return;

    mount()?.appendChild(groupWith('work', works.heading, works.rows));
  },
};
